import { useIntl } from "react-intl";

import { RanaTable } from "@nens/ranalib";

import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { Profile } from "@/lib/profile";
import { RanaFileIcon, isPublicationFile } from "@/lib/rana-file-data-type";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { CompleteFile } from "../../..";
import { usePublicationStore } from "../../../store";
import { getFiles } from "../../../store/selectors";
import { TableEmptyPublicationFiles } from "../../detail/components/table-empty-files";
import { getUrlsForDescriptor } from "../utils/get-urls-for-descriptor";

import { FileAsUrlsPanels } from "./file-as-urls-panels";

function filterFileReference(fileReference: CompleteFile, searchString: string): boolean {
  if (!searchString) return true;

  return fileReference.path.toLowerCase().includes(searchString.toLowerCase());
}

const FileAsUrlsRow = ({ completeFile }: { completeFile: CompleteFile }) => {
  const intl = useIntl();
  const { path, file, name } = completeFile;
  const fallbackName = getNameFromFileId(path);

  const urlsWithLabels = getUrlsForDescriptor(completeFile.file.descriptor);

  const hasNoUrls = urlsWithLabels.length === 0;

  return (
    <RanaTable.Row.Accordion>
      <RanaTable.Row.Accordion.Button
        rowId={path}
        isDisabled={hasNoUrls}
        title={
          hasNoUrls
            ? intl.formatMessage({
                id: "publication.file_as_url.no_urls_available",
                defaultMessage: "No URLs available for this file",
              })
            : undefined
        }
      >
        <RanaTable.Cell title={name ?? fallbackName}>
          <RanaFileIcon fileOrDirectory={file} />
          {name ?? fallbackName}
        </RanaTable.Cell>
        <RanaTable.Cell>
          <Profile inTable user={file.user} />
        </RanaTable.Cell>
      </RanaTable.Row.Accordion.Button>

      <FileAsUrlsPanels urls={urlsWithLabels} />
    </RanaTable.Row.Accordion>
  );
};

export function FilesAsUrlsTable() {
  const intl = useIntl();
  const { searchString, setSearchString } = useRanaTableData({ keyAddon: "publicationFiles" });

  const fileReferences = usePublicationStore(getFiles);

  const publicationFileReferences = fileReferences
    .filter((fileReference) => filterFileReference(fileReference, searchString))
    .sort(({ file }) => (isPublicationFile(file) ? -1 : 1));

  const tableDataIds = publicationFileReferences.map(({ path }) => path);

  return (
    <RanaTable
      hasAccordion
      columnDefinitions={[
        {
          width: "table_cell",
          title: intl.formatMessage({
            id: "publication.detail.file.column.name",
            defaultMessage: "Name",
          }),
          orderingField: "id",
        },

        {
          title: intl.formatMessage({
            id: "publication.detail.file.column.added_by",
            defaultMessage: "Added by",
          }),
          width: "table_cell_xs",
        },
      ]}
      tableDataIds={tableDataIds}
      emptyElement={
        <TableEmptyPublicationFiles hasSearchString clearSearchString={() => setSearchString("")} />
      }
    >
      <RanaTable.Filters
        search={{
          placeholder: intl.formatMessage({
            id: "search.publication_file",
            defaultMessage: "Find file",
          }),
          searchString,
          setSearchString,
        }}
      />

      <RanaTable.Container>
        <RanaTable.Header />
        <RanaTable.Body>
          {publicationFileReferences.map((completeFile) => (
            <FileAsUrlsRow key={completeFile.path} completeFile={completeFile} />
          ))}
        </RanaTable.Body>
      </RanaTable.Container>
    </RanaTable>
  );
}
